import React from 'react';
import type { PillKind } from './Pill';

interface NoticeProps {
  kind?: PillKind;
  children: React.ReactNode;
  /**
   * Optional action button (typically a "Retry" or "Dismiss").
   * Rendered to the right of the message text.
   */
  action?: React.ReactNode;
  /**
   * Inline margins for layout convenience. Most surfaces pass `marginTop: '10px'`
   * via inline style; preserved here as a prop so callers don't keep inlining.
   */
  marginTop?: number | string;
  className?: string;
}

/**
 * Inline notice component for one-line status messages, errors, and
 * success confirmations. Visually equivalent to the existing
 * `pill <kind>` styling but with semantic intent (notice, not label).
 *
 * For short status labels, prefer `<Pill>`. For multi-line errors with
 * retry actions, callers can compose `<Notice kind="warning" action={...}>`.
 */
export const Notice = ({
  kind = 'neutral',
  children,
  action,
  marginTop,
  className
}: NoticeProps): JSX.Element => {
  const variantClass = kind === 'neutral' ? '' : ` ${kind}`;
  const composedClassName = `pill${variantClass}${className ? ` ${className}` : ''}`;
  const style: React.CSSProperties | undefined =
    marginTop !== undefined ? { marginTop } : undefined;
  if (action) {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', ...(style ?? {}) }} role="status">
        <span className={composedClassName}>{children}</span>
        {action}
      </div>
    );
  }
  return (
    <div className={composedClassName} style={style} role="status">
      {children}
    </div>
  );
};
